import {
  Column,
  Container,
  Header,
  Layout,
  List,
  Row,
  SettingsSelect,
} from '@/components/SettingsLayout';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './styles.module.css';
import { Icon, IconButton, IconType, SettingsLink, Text } from '@/ui';
import CompanySkeleton from './components/CompanySkeleton';
import { companyRef } from './Companies';
import SingleCompanyContent from './SingleCompanyContent';
import BigScreenList from '@/components/SettingsLayout/List/BigScreenList';
import { CompanyLinks } from './types';
import ImageWithFallback from '@/ui/ImageWithFallBack/ImageWithFallBack';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetCompanyByIdQuery } from '@/store/slices/companySlice';
import { useGetUser } from '@/hooks';
import { useGetPlanByCompanyIdQuery } from '@/store/slices/plansSlice';

const SingleCompany = () => {
  const { id, info } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useGetUser();
  const { data: singlePlan, isLoading: planLoading } = useGetPlanByCompanyIdQuery(
    id && currentUser?.companyId
      ? { companyId: id, organizationId: currentUser.companyId }
      : skipToken,
  );

  const {
    data: company,
    isLoading,
    isError,
  } = useGetCompanyByIdQuery(
    id && currentUser ? { id, organizationId: currentUser.companyId } : skipToken,
  );

  useEffect(() => {
    if (!info) navigate(`${companyRef}/${id}/${CompanyLinks.DASHBOARD}`);
    else navigate(`${companyRef}/${id}/${info}`);
  }, [info, navigate, id]);

  if (isError) {
    const message = t('Error.default');
    throw new Error(message);
  }

  const selectOptions = useMemo(() => {
    return Object.values(CompanyLinks).map((value) => {
      return {
        id: value,
        text: t(`Companies.${value}`),
      };
    });
  }, []);

  if (isLoading || planLoading) {
    return (
      <Layout>
        <Container size='lg'>
          <CompanySkeleton />
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Column gapSize='16' fullWidth>
          <Header>
            <div className={styles.headerInfoHolder}>
              <IconButton onClick={() => navigate(companyRef)}>
                <Icon type={IconType.ArrowLeftLong} width={24} height={24} fill='#3D4C66' />
              </IconButton>
              <div className={styles.headerText}>
                <Text size='l' color='base-blue' weight='semi-bold'>
                  {company?.name}
                </Text>
              </div>
            </div>
            <SettingsSelect
              className='headerSelect'
              options={selectOptions}
              href={`${companyRef}/${company?._id}`}
            />
          </Header>
          <div className={styles.companyLogo}>
            <ImageWithFallback src={company?.logo} />
          </div>
          <Column gapSize='12' align='center'>
            <div className={styles.orgInfoDiv}>
              <Row gapSize='12' justify='justify-between' align='start'>
                <Row noShrink fullWidth={false}>
                  <Text size='s' weight='light-medium'>
                    {t('Companies.managed_by')}
                  </Text>
                </Row>
                <Text size='s' weight='light-medium' className='text-right'>
                  {company?.organization?.name}
                </Text>
              </Row>
              <Row gapSize='12' justify='justify-between' align='start'>
                <Text size='s' weight='light-medium'>
                  {t('Companies.account_status')}
                </Text>
                <Text size='s' weight='light-medium' color='teal'>
                  {t('Shared.active')}
                </Text>
              </Row>
              <Row gapSize='12' justify='justify-between' align='start'>
                <Text size='s' weight='light-medium'>
                  {t('Companies.plan')}
                </Text>
                <Text size='s' weight='light-medium' className='text-right'>
                  {singlePlan?.name}
                </Text>
              </Row>
            </div>
            {/* <Button variant='primary' full href=''>
              <Row gapSize='8' align='center' justify='justify-center'>
                {t('Companies.open_hub')}
                <Icon type={IconType.NewWindow} width={24} fill='transparent' />
              </Row>
            </Button> */}
          </Column>
          <SettingsSelect
            className='bodySelect'
            options={selectOptions}
            href={`${companyRef}/${company?._id}`}
          />
          <BigScreenList>
            <List>
              <List.Header>
                <div />
              </List.Header>
              <List.Body>
                <li key={CompanyLinks.DASHBOARD}>
                  <SettingsLink
                    href={`${companyRef}/${id}/${CompanyLinks.DASHBOARD}`}
                    text={t('Navigation.dashboard')}
                    svg={IconType.DropDownIcon}
                    svgRotate={'-90'}
                  />
                </li>
                <li key={CompanyLinks.PROFILE}>
                  <SettingsLink
                    href={`${companyRef}/${id}/${CompanyLinks.PROFILE}`}
                    text={t('Companies.profile')}
                    svg={IconType.DropDownIcon}
                    svgRotate={'-90'}
                  />
                </li>
                {/* TODO: Add plan link back when we  custom plan page */}
                <li key={CompanyLinks.PLAN}>
                  <SettingsLink
                    href={`${companyRef}/${id}/${CompanyLinks.PLAN}`}
                    text={t('Companies.plan')}
                    svg={IconType.DropDownIcon}
                    svgRotate={'-90'}
                  />
                </li>
                {
                  <li key={CompanyLinks.PRICING}>
                    <SettingsLink
                      href={`${companyRef}/${id}/${CompanyLinks.PRICING}`}
                      text={t('Companies.pricing')}
                      svg={IconType.DropDownIcon}
                      svgRotate={'-90'}
                    />
                  </li>
                }
              </List.Body>
            </List>
          </BigScreenList>
        </Column>
      </Container>
      <Container size='lg'>
        {isLoading && <CompanySkeleton />}
        {!isLoading && company && <SingleCompanyContent company={company} />}
      </Container>
    </Layout>
  );
};

export default SingleCompany;
